import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Upload, Trash2, Copy, Image, Film, Loader2,
  CheckCircle, AlertCircle, X, Filter
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminMedia = () => {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [filter, setFilter] = useState('all'); // all, image, video
  const [dragOver, setDragOver] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchMedia();
  }, []);

  const fetchMedia = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/media?page_size=500`);
      setMedia(res.data.data ?? res.data);
    } catch {
      toast.error('Failed to load media library');
    } finally {
      setLoading(false);
    }
  };

  const uploadFiles = async (files) => {
    if (!files.length) return;
    setUploading(true);
    let succeeded = 0;
    let failed = 0;
    for (const file of files) {
      const form = new FormData();
      form.append('file', file);
      try {
        await axios.post(`${API_URL}/api/admin/media/upload`, form, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        succeeded++;
      } catch (err) {
        failed++;
        const detail = err.response?.data?.detail || file.name;
        toast.error(`Failed: ${detail}`);
      }
    }
    if (succeeded > 0) toast.success(`${succeeded} file${succeeded > 1 ? 's' : ''} uploaded`);
    await fetchMedia();
    setUploading(false);
  };

  const handleFileChange = (e) => {
    uploadFiles(Array.from(e.target.files || []));
    e.target.value = '';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    uploadFiles(Array.from(e.dataTransfer.files));
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item.original_name}"?`)) return;
    setDeletingId(item.id);
    try {
      await axios.delete(`${API_URL}/api/admin/media/${item.id}`);
      setMedia(prev => prev.filter(m => m.id !== item.id));
      toast.success('File deleted');
    } catch {
      toast.error('Failed to delete file');
    } finally {
      setDeletingId(null);
    }
  };

  const copyUrl = (url) => {
    const full = url.startsWith('http') ? url : `${API_URL}${url}`;
    navigator.clipboard.writeText(full).then(() => toast.success('URL copied to clipboard'));
  };

  const filtered = media.filter(m => filter === 'all' || m.type === filter);

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-[#1A2E6E] font-['Sora']">Media Library</h1>
          <p className="text-[#1A2E6E]/60 mt-1">Upload and manage images and videos for your site</p>
        </div>
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="flex items-center gap-2 px-5 py-2.5 bg-[#D72B2B] text-white rounded-xl hover:bg-[#B71C1C] transition-colors font-medium disabled:opacity-50"
        >
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          Upload Files
        </button>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*,video/mp4,video/webm,video/ogg"
          className="hidden"
          onChange={handleFileChange}
        />
      </div>

      {/* Drop Zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
          dragOver ? 'border-[#D72B2B] bg-[#D72B2B]/5' : 'border-[#E8EAF0] hover:border-[#1A2E6E]/30 hover:bg-[#1A2E6E]/2'
        }`}
      >
        <Upload className="w-8 h-8 text-[#1A2E6E]/30 mx-auto mb-2" />
        <p className="text-sm text-[#1A2E6E]/60">
          {dragOver ? 'Drop files here' : 'Drag & drop files here, or click to select'}
        </p>
        <p className="text-xs text-[#1A2E6E]/40 mt-1">Images (JPG, PNG, GIF, WebP, SVG) · Videos (MP4, WebM) · Max 100 MB per file</p>
      </div>

      {/* Filter + count */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex gap-2 bg-white rounded-xl p-1.5 border border-[#E8EAF0]">
          {[
            { id: 'all', label: 'All' },
            { id: 'image', label: 'Images' },
            { id: 'video', label: 'Videos' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setFilter(tab.id)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                filter === tab.id
                  ? 'bg-[#1A2E6E] text-white'
                  : 'text-[#1A2E6E]/70 hover:text-[#1A2E6E] hover:bg-[#1A2E6E]/5'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <span className="text-sm text-[#1A2E6E]/50">{filtered.length} file{filtered.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-48">
          <Loader2 className="w-8 h-8 text-[#D72B2B] animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-xl border border-[#E8EAF0] p-12 text-center">
          <Image className="w-16 h-16 text-[#1A2E6E]/20 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-[#1A2E6E] mb-2">No files yet</h3>
          <p className="text-[#1A2E6E]/60 text-sm">Upload images or videos to use them across your site</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          <AnimatePresence>
            {filtered.map((item) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="bg-white rounded-xl border border-[#E8EAF0] overflow-hidden group hover:shadow-md transition-shadow"
              >
                {/* Preview */}
                <div className="relative bg-[#F8F9FC] aspect-square overflow-hidden">
                  {item.type === 'image' ? (
                    <img
                      src={item.url.startsWith('http') ? item.url : `${API_URL}${item.url}`}
                      alt={item.original_name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <Film className="w-10 h-10 text-[#1A2E6E]/30" />
                    </div>
                  )}
                  {/* Overlay actions */}
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <button
                      onClick={() => copyUrl(item.url)}
                      className="p-2 bg-white rounded-lg hover:bg-[#F5A623] hover:text-white transition-colors"
                      title="Copy URL"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(item)}
                      disabled={deletingId === item.id}
                      className="p-2 bg-white rounded-lg hover:bg-[#D72B2B] hover:text-white transition-colors disabled:opacity-50"
                      title="Delete"
                    >
                      {deletingId === item.id
                        ? <Loader2 className="w-4 h-4 animate-spin" />
                        : <Trash2 className="w-4 h-4" />
                      }
                    </button>
                  </div>
                  {/* Type badge */}
                  <span className={`absolute top-2 left-2 px-2 py-0.5 rounded-full text-xs font-medium ${
                    item.type === 'video' ? 'bg-[#1A2E6E]/80 text-white' : 'bg-white/80 text-[#1A2E6E]'
                  }`}>
                    {item.type === 'video' ? 'Video' : 'Image'}
                  </span>
                </div>

                {/* Info */}
                <div className="p-3">
                  <p className="text-xs font-medium text-[#1A2E6E] truncate" title={item.original_name}>
                    {item.original_name}
                  </p>
                  <p className="text-xs text-[#1A2E6E]/50 mt-0.5">{formatSize(item.size)}</p>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
};

export default AdminMedia;
